import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Calendar, Eye, Download, Mail, Loader2, Scale, CheckCircle2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { DocumentPreviewDialog } from "@/components/documents/DocumentPreviewDialog";
import DocumentEmailWizard from "@/components/documents/DocumentEmailWizard";

interface Resolution {
  id: string;
  una_id: string;
  task_id: string | null;
  resolution_title: string;
  resolution_category: string;
  resolution_date: string;
  filed_date: string | null;
  whereas_clauses: string[];
  resolved_clauses: string[];
  implementation_notes: string | null;
  status: string;
}

interface ResolutionDetailModalProps {
  resolution: Resolution | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unaId: string;
}

interface Document {
  id: string;
  document_name: string;
  document_type: string;
  status: string;
  file_url: string | null;
  signed_file_url: string | null;
  generated_at: string | null;
}

export function ResolutionDetailModal({ 
  resolution, 
  open, 
  onOpenChange, 
  unaId 
}: ResolutionDetailModalProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [emailDocId, setEmailDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  useEffect(() => {
    if (open && resolution?.task_id) {
      loadResolutionDocuments();
    }
  }, [open, resolution?.task_id]);

  const loadResolutionDocuments = async () => {
    if (!resolution?.task_id) return;
    
    setIsLoading(true);
    try {
      const { data: docs, error } = await supabase
        .from('generated_documents')
        .select('id, document_name, document_type, status, file_url, signed_file_url, generated_at')
        .eq('task_id', resolution.task_id)
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(docs || []);
    } catch (error) {
      console.error('Error loading resolution documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!resolution) return null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not specified';
    try {
      return format(parseISO(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Governance': 'bg-blue-100 text-blue-800',
      'Financial': 'bg-green-100 text-green-800',
      'Asset Management': 'bg-purple-100 text-purple-800',
      'Membership': 'bg-yellow-100 text-yellow-800',
      'Operations': 'bg-slate-100 text-slate-800',
      'Policy': 'bg-orange-100 text-orange-800',
      'Appointment': 'bg-red-100 text-red-800',
      'Authorization': 'bg-blue-100 text-blue-800',
      'Approval': 'bg-green-100 text-green-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getDocumentUrl = (doc: Document) => {
    return doc.signed_file_url || doc.file_url;
  };

  const handleDownload = async (doc: Document) => {
    const url = getDocumentUrl(doc);
    if (!url) return;
    
    setDownloadingDocId(doc.id);
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const filename = `${doc.document_name?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      window.open(url, '_blank');
    } finally {
      setDownloadingDocId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pr-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-xl">{resolution.resolution_title}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Effective: {formatDate(resolution.resolution_date)}
                </p>
              </div>
              <Badge className={getCategoryColor(resolution.resolution_category)}>
                {resolution.resolution_category}
              </Badge>
            </div>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6 pt-4">
              {/* Resolution Details Grid */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-primary" />
                  Resolution Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Effective Date</p>
                    <p className="font-medium">{formatDate(resolution.resolution_date)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Filed Date</p>
                    <p className="font-medium">{formatDate(resolution.filed_date)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-700 capitalize">{resolution.status}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* WHEREAS Clauses */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Scale className="h-4 w-4 text-primary" />
                  WHEREAS Clauses ({resolution.whereas_clauses?.length || 0})
                </h3>
                {resolution.whereas_clauses && resolution.whereas_clauses.length > 0 ? (
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    {resolution.whereas_clauses.map((clause, index) => (
                      <li key={index} className="text-muted-foreground pl-2">
                        <span className="text-foreground">{clause}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-muted-foreground">No WHEREAS clauses specified</p>
                )}
              </div>

              <Separator />

              {/* RESOLVED Clauses */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  RESOLVED Clauses ({resolution.resolved_clauses?.length || 0})
                </h3>
                {resolution.resolved_clauses && resolution.resolved_clauses.length > 0 ? (
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    {resolution.resolved_clauses.map((clause, index) => (
                      <li key={index} className="text-muted-foreground pl-2">
                        <span className="text-foreground">{clause}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-muted-foreground">No RESOLVED clauses specified</p>
                )}
              </div>

              {/* Implementation Notes */}
              {resolution.implementation_notes && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-2">Implementation Notes</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {resolution.implementation_notes}
                    </p>
                  </div>
                </>
              )}

              <Separator />

              {/* Associated Documents */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-primary" />
                  Associated Documents ({documents.length})
                </h3>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <Card key={doc.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <p className="font-medium truncate">{doc.document_name}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {doc.generated_at && `Generated ${format(parseISO(doc.generated_at), 'MMM d, yyyy')}`}
                              </p>
                            </div>
                            <Badge 
                              variant={doc.status === 'final' || doc.status === 'finalized' ? 'default' : 'secondary'}
                              className="flex-shrink-0"
                            >
                              {doc.status === 'final' || doc.status === 'finalized' ? 'Final' : doc.status}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDocId(doc.id)}
                              className="min-h-[44px] sm:min-h-0"
                            >
                              <Eye className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Preview</span>
                            </Button>
                            {getDocumentUrl(doc) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingDocId === doc.id}
                                className="min-h-[44px] sm:min-h-0"
                              >
                                {downloadingDocId === doc.id ? (
                                  <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4 sm:mr-1" />
                                )}
                                <span className="hidden sm:inline">
                                  {downloadingDocId === doc.id ? 'Downloading...' : 'Download'}
                                </span>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEmailDocId(doc.id)}
                              className="min-h-[44px] sm:min-h-0"
                            >
                              <Mail className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Email</span>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents associated with this resolution</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        documentId={previewDocId}
        open={!!previewDocId}
        onClose={() => setPreviewDocId(null)}
      />

      {/* Document Email Wizard */}
      {emailDocId && resolution?.task_id && (
        <DocumentEmailWizard
          open={!!emailDocId}
          onOpenChange={(open) => !open && setEmailDocId(null)}
          taskId={resolution.task_id}
          unaId={unaId}
          preSelectedDocumentId={emailDocId}
          onSuccess={() => setEmailDocId(null)}
        />
      )}
    </>
  );
}